import { ChangeEvent, FC } from 'react';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import { IconButton, InputAdornment } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';

import { Input, InputProps } from '../Input';
import { FormElementProps } from '../types';

export interface FormCurrencyFieldProps
  extends Omit<InputProps, 'name' | 'onChange'>,
    FormElementProps {
  handleChange?: (name: string, value: string | number) => void;
  clearable?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  'data-testid'?: string;
}

export const FormCurrencyField: FC<FormCurrencyFieldProps> = (props) => {
  const {
    name,
    rules,
    defaultValue,
    handleChange,
    inputRef,
    clearable,
    'data-testid': dataTestId,
    ...rest
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name) as FieldError;

  return (
    <Controller
      control={control}
      defaultValue={defaultValue || ''}
      name={name}
      render={({ field }: any) => (
        <Input
          {...rest}
          {...field}
          {...(inputRef ? { ref: inputRef } : {})}
          inputProps={{
            ...(dataTestId ? { 'data-testid': dataTestId } : {}),
            inputMode: 'decimal',
          }}
          error={error?.message}
          name={name}
          startAdornment={
            <InputAdornment
              position="start"
              sx={{
                backgroundColor: '#e9ecef',
                m: 0,
                px: 2,
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRight: '1px solid',
                borderColor: 'divider',
                marginRight: 1,
                maxHeight: '100%',
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
              }}
            >
              <span style={{ fontSize: '1.25rem', color: '#2f4a57' }}>$</span>
            </InputAdornment>
          }
          sx={{
            '& .MuiInputBase-root': {
              padding: 0,
            },
          }}
          endAdornment={
            clearable && field.value ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    field.onChange('');
                    if (handleChange) handleChange(name, '');
                  }}
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          onChange={(
            event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            const raw = event.target.value;
            const numeric = raw.replace(/[^0-9.]/g, '');
            if (handleChange) handleChange(event.target.name, numeric);
            field.onChange(numeric);
          }}
          value={
            field.value
              ? Number(field.value).toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })
              : ''
          }
        />
      )}
      rules={rules}
    />
  );
};
